import React from 'react';

export interface VehicleRow {
  vatNumber: string;
  licensePlate: string;
  status?: string;
  searchDate?: string;
  vehicleType?: string;
  insuranceCompany?: string;
  coverageStart?: string;
  coverageEnd?: string;
  message?: string;
}

export interface ProcessingState {
  isProcessing: boolean;
  progress: number;
  currentRow: number;
  totalRows: number;
}

export enum InsuranceStatus {
  INSURED = 'ΑΣΦΑΛΙΣΜΕΝΟ',
  UNINSURED = 'ΑΝΑΣΦΑΛΙΣΤΟ',
  NOT_FOUND = 'ΔΕΝ ΒΡΕΘΗΚΕ',
  ERROR = 'ΣΦΑΛΜΑ',
  FAILED = 'ΑΠΟΤΥΧΙΑ'
}

export const getStatusIcon = (status?: string): React.ReactNode => {
  if (!status) {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
      React.createElement('line', { x1: 12, y1: 6, x2: 12, y2: 12 }),
      React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
    );
  }
  
  const normalizedStatus = status.toUpperCase();
  
  if (normalizedStatus.includes('ΑΣΦΑΛΙΣΜΕΝΟ') || normalizedStatus === 'INSURED') {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('path', { d: 'M20 6L9 17l-5-5' })
    );
  }
  if (normalizedStatus.includes('ΑΝΑΣΦΑΛΙΣΤΟ') || normalizedStatus === 'UNINSURED') {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('path', { d: 'M12 9v4M12 17h.01' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 10 })
    );
  }
  if (normalizedStatus.includes('ΔΕΝ ΒΡΕΘΗΚΕ') || normalizedStatus === 'NOT_FOUND') {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
      React.createElement('path', { d: 'm21 21-4.35-4.35' })
    );
  }
  if (normalizedStatus.includes('ΣΦΑΛΜΑ') || normalizedStatus === 'ERROR') {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
      React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
      React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
    );
  }
  if (normalizedStatus.includes('ΑΠΟΤΥΧΙΑ') || normalizedStatus === 'FAILED') {
    return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
      React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
      React.createElement('line', { x1: 15, y1: 9, x2: 9, y2: 15 }),
      React.createElement('line', { x1: 9, y1: 9, x2: 15, y2: 15 })
    );
  }
  
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
    React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
    React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
    React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
  );
};

export const getStatusColor = (status?: string): string => {
  if (!status) return '#9CA3AF';
  
  const normalizedStatus = status.toUpperCase();
  
  if (normalizedStatus.includes('ΑΣΦΑΛΙΣΜΕΝΟ') || normalizedStatus === 'INSURED') {
    return '#4CAF50'; // Insurance green
  }
  if (normalizedStatus.includes('ΑΝΑΣΦΑΛΙΣΤΟ') || normalizedStatus === 'UNINSURED') {
    return '#FF9800'; // Vibrant orange
  }
  if (normalizedStatus.includes('ΔΕΝ ΒΡΕΘΗΚΕ') || normalizedStatus === 'NOT_FOUND') {
    return '#607D8B'; // Blue-gray
  }
  if (normalizedStatus.includes('ΣΦΑΛΜΑ') || normalizedStatus === 'ERROR') {
    return '#F44336'; // Bright red
  }
  if (normalizedStatus.includes('ΑΠΟΤΥΧΙΑ') || normalizedStatus === 'FAILED') {
    return '#D32F2F'; // Deep red
  }
  
  return '#9CA3AF';
};

export const getStatusBackground = (status?: string): string => {
  if (!status) return '#F3F4F6';
  
  const normalizedStatus = status.toUpperCase();
  
  if (normalizedStatus.includes('ΑΣΦΑΛΙΣΜΕΝΟ') || normalizedStatus === 'INSURED') {
    return '#E8F5E9'; // Light green
  }
  if (normalizedStatus.includes('ΑΝΑΣΦΑΛΙΣΤΟ') || normalizedStatus === 'UNINSURED') {
    return '#FFF3E0'; // Light orange
  }
  if (normalizedStatus.includes('ΔΕΝ ΒΡΕΘΗΚΕ') || normalizedStatus === 'NOT_FOUND') {
    return '#ECEFF1'; // Light blue-gray
  }
  if (normalizedStatus.includes('ΣΦΑΛΜΑ') || normalizedStatus === 'ERROR') {
    return '#FFEBEE'; // Light red
  }
  if (normalizedStatus.includes('ΑΠΟΤΥΧΙΑ') || normalizedStatus === 'FAILED') {
    return '#FFEBEE'; // Light red
  }
  
  return '#F3F4F6';
};

export const getStatusLabel = (status?: string): string => {
  if (!status) return 'Εκκρεμεί';
  
  const normalizedStatus = status.toUpperCase();
  
  if (normalizedStatus.includes('ΑΣΦΑΛΙΣΜΕΝΟ') || normalizedStatus === 'INSURED') {
    return 'ΑΣΦΑΛΙΣΜΕΝΟ';
  }
  if (normalizedStatus.includes('ΑΝΑΣΦΑΛΙΣΤΟ') || normalizedStatus === 'UNINSURED') {
    return 'ΑΝΑΣΦΑΛΙΣΤΟ';
  }
  if (normalizedStatus.includes('ΔΕΝ ΒΡΕΘΗΚΕ') || normalizedStatus === 'NOT_FOUND') {
    return 'ΔΕΝ ΒΡΕΘΗΚΕ';
  }
  if (normalizedStatus.includes('ΣΦΑΛΜΑ') || normalizedStatus === 'ERROR') {
    return 'ΣΦΑΛΜΑ';
  }
  if (normalizedStatus.includes('ΑΠΟΤΥΧΙΑ') || normalizedStatus === 'FAILED') {
    return 'ΑΠΟΤΥΧΙΑ';
  }
  
  return status;
};
